import { useEffect, useState } from 'react';
import {
  Typography,
  Divider,
  Link,
  TextField,
  Box,
  useTheme,
  IconButton,
  Stack,
  Grid,
  MenuItem,
} from '@mui/material';
import { FormikProps } from 'formik';
import type { InvoiceCreateInput, TaxCode } from '@repo/api-contract';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import { gridItemProps, textFieldProps } from '../../constants/form';
import {
  getFormikField,
  onNumberFieldBlur,
  onNumberFieldFocus,
} from '../../../../util/fornik-helpers';

export default function Customer({
  formik,
  viewMode,
  taxCodes,
}: {
  formik: FormikProps<InvoiceCreateInput>;
  viewMode: boolean;
  taxCodes: TaxCode[];
}): JSX.Element {
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const { t: tAddI } = useTranslation([], {
    keyPrefix: 'team.Modals.Invoices.createAndEdit.Add Invoice',
  });

  const theme = useTheme();
  const dispatch = useDispatch();

  const { formData } = useSelector(
    ({ invoiceModalSlice }) => invoiceModalSlice,
  );

  const [customerPrice, setCustomerPrice] = useState<number>(0);
  const [tax, setTax] = useState<number>(0);

  useEffect(() => {
    const sum = formData.items.reduce(
      (acc, item) => acc + item.customerPrice * item.quantityOrdered,
      0,
    );
    const totalCustomerPrice = Math.round(sum * 100) / 100;
    setCustomerPrice(totalCustomerPrice);

    const taxRate = (formData.taxCode?.rate || 0) / 100;
    let totalTax = formData.items.reduce((acc, item) => {
      if (item.taxable) {
        return acc + item.customerPrice * item.quantityOrdered * taxRate;
      }
      return acc;
    }, 0);
    totalTax = Math.round(totalTax * 100) / 100;
    setTax(totalTax);
  }, [formData.items, formData.taxCode]);

  const totalInvoice =
    customerPrice +
    tax +
    Number(getFormikField(formik, 'customerFreight').value);
  const customerBalance =
    Math.round(
      (totalInvoice - (getFormikField(formik, 'customerDeposit').value || 0)) *
        100,
    ) / 100;

  return (
    <>
      <Typography
        color="primary"
        className="flex justify-between"
        component="div"
      >
        <span>{tAddI('CUSTOMER INFO')}</span>
        <Box className="flex">
          <Link
            className="mx-2"
            color="text.secondary"
            underline="none"
            sx={{ cursor: 'pointer' }}
          >
            {tAddI('CUSTOMER TOTAL') + ':'}
          </Link>
          <span className="font-bold text-black">
            {'$' + totalInvoice.toFixed(2)}
          </span>
        </Box>
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}></Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Customer Price')}
              name="customerPrice"
              type="number"
              value={customerPrice.toFixed(2)}
            />
          </Grid>
        </Stack>
      </Stack>
      <Stack
        gap={2}
        marginY={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps} className="justify-end">
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddI('Sales Tax Code')}
              select
              name="taxCode"
              value={formData.taxCode?.TaxCodeId || ''}
              error={Boolean(getFormikField(formik, 'taxCode.TaxCodeId').error)}
              helperText={getFormikField(formik, 'taxCode.TaxCodeId').error}
              onChange={(event) => {
                const selectedTC = taxCodes.find(
                  (tC) => tC.TaxCodeId === event.target.value,
                );
                formik.setFieldValue('taxCode', selectedTC);
              }}
            >
              <MenuItem value={''}>{tGC('None')}</MenuItem>
              {taxCodes.map((tC) => {
                return (
                  <MenuItem key={tC.TaxCodeId} value={tC.TaxCodeId}>
                    {tC.codeName}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>
        </Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Sales Tax')}
              name="code"
              type="number"
              value={tax.toFixed(2)}
            />
          </Grid>
        </Stack>
      </Stack>
      <Stack
        gap={2}
        direction={'row'}
        justifyContent={'space-around'}
        width={'100%'}
      >
        <Stack gap={2} width={'100%'}></Stack>
        <Stack gap={2} width={'100%'}>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled={viewMode}
              {...textFieldProps}
              label={tAddI('Customer Freight')}
              name="customerFreight"
              type="number"
              onFocus={onNumberFieldFocus(formik, 'customerFreight')}
              onBlur={onNumberFieldBlur(formik, 'customerFreight')}
              error={Boolean(getFormikField(formik, 'customerFreight').error)}
              helperText={getFormikField(formik, 'customerFreight').error}
              value={getFormikField(formik, 'customerFreight').value}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Total Invoice')}
              name="totalInvoice"
              type="number"
              value={totalInvoice.toFixed(2)}
              onChange={formik.handleChange}
            />
          </Grid>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Customer Deposit')}
              name="customerDeposit"
              type="number"
              value={getFormikField(formik, 'customerDeposit').value.toFixed(2)}
            />
          </Grid>
          <Grid {...gridItemProps} className="justify-start">
            <TextField
              variant="filled"
              disabled
              {...textFieldProps}
              label={tAddI('Customer Balance')}
              name="customerBalance"
              type="number"
              value={customerBalance.toFixed(2)}
            />
          </Grid>
        </Stack>
      </Stack>
    </>
  );
}
